import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { extractTemplateVariables } from './variables.js';
import { notificationTemplateEndpoints } from './endpoints.js';
import {
  createdNotificationTemplate,
  notificationTemplateDetail,
  type CreatedNotificationTemplate,
  type NotificationTemplateDetail,
  type NotificationTemplateFormInput,
} from './schemas.js';

/**
 * Writes for the Notification Templates module.
 *
 * Each mutation invalidates the lists and details. Create is idempotent through
 * the Gateway; update is a full PUT because the backend takes a partial and the
 * form owns every field it sends.
 */
const WRITTEN_KEYS = [
  queryKeys.notificationTemplates.lists,
  queryKeys.notificationTemplates.details,
];

function createTemplatePayload(values: NotificationTemplateFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    channel: values.channel,
    code: values.code,
    type: values.code,
    name: values.name,
    body: values.body,
    variables: [...extractTemplateVariables(values.body)],
    isDefault: values.isDefault,
    isActive: values.isActive,
  };

  if (values.propertyId !== '') payload.propertyId = values.propertyId;

  if (values.channel === 'EMAIL') {
    payload.subject = values.subject;
    payload.fromAddress = values.fromAddress === '' ? null : values.fromAddress;
    payload.fromName = values.fromName === '' ? null : values.fromName;
  }

  return payload;
}

function updateTemplatePayload(values: NotificationTemplateFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: values.name,
    body: values.body,
    variables: [...extractTemplateVariables(values.body)],
    isDefault: values.isDefault,
    isActive: values.isActive,
  };

  if (values.channel === 'EMAIL') {
    payload.subject = values.subject;
    payload.fromAddress = values.fromAddress === '' ? null : values.fromAddress;
    payload.fromName = values.fromName === '' ? null : values.fromName;
  }

  return payload;
}

export function useCreateNotificationTemplate(): UseMutationResult<
  CreatedNotificationTemplate,
  unknown,
  NotificationTemplateFormInput
> {
  return useApiMutation<NotificationTemplateFormInput, CreatedNotificationTemplate>({
    mutationFn: async (api, input) => {
      const payload = createTemplatePayload(input);
      const response = await api.post<unknown>(
        notificationTemplateEndpoints.create,
        payload,
        crypto.randomUUID(),
      );
      return parseOrThrow(
        createdNotificationTemplate,
        response,
        notificationTemplateEndpoints.create,
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateNotificationTemplateVariables {
  readonly templateId: string;
  readonly values: NotificationTemplateFormInput;
}

export function useUpdateNotificationTemplate(): UseMutationResult<
  NotificationTemplateDetail,
  unknown,
  UpdateNotificationTemplateVariables
> {
  return useApiMutation<UpdateNotificationTemplateVariables, NotificationTemplateDetail>({
    mutationFn: async (api, { templateId, values }) => {
      const payload = updateTemplatePayload(values);
      const response = await api.put<unknown>(
        notificationTemplateEndpoints.update(templateId),
        payload,
      );
      return parseOrThrow(
        notificationTemplateDetail,
        response,
        notificationTemplateEndpoints.update(templateId),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeleteNotificationTemplate(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, templateId) => {
      await api.delete<unknown>(notificationTemplateEndpoints.delete(templateId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
